import { apiFetchJSON } from '../../core/api.js';
import { escapeHtmlText } from '../../core/format.js';
import {
  chartOptionsForPing,
  closeInfraChart,
  getOpenInfraChartKey,
  INFRA_CHART_PRESETS,
  mergeChartHistory,
  openInfraChart,
  refreshOpenInfraChart,
  type ChartPoint
} from './chart.js';
import { setBarWidthSmooth, tweenNumber } from './smooth.js';
import { bindRollCounter } from './roll-counter.js';
import { formatMaintenanceElapsed } from '../../../../src/lib/maintenance-elapsed.js';

type InfraCheckStatus = 'ok' | 'warn' | 'error' | 'unknown';

interface HostMetricsBlock {
  role?: string;
  hostname: string;
  platform: string;
  uptimeSec: number;
  loadPercent: number | null;
  loadAvg1: number;
  cpuCount: number;
  memoryUsedPercent: number;
  memoryUsedMb: number;
  memoryTotalMb: number;
  temperatureC: number | null;
  temperatureNote?: string | null;
  processUptimeSec: number;
  collectedAt?: string;
  ageSec?: number | null;
  stale?: boolean;
}

interface InfraCheck {
  id: string;
  name: string;
  group: string;
  status: InfraCheckStatus;
  latencyMs: number | null;
  detail: string;
}

interface InfraStatusResponse {
  ok: boolean;
  healthy?: boolean;
  collectedAt?: string;
  summary?: {
    overall: 'ok' | 'degraded' | 'down';
    okCount: number;
    warnCount: number;
    errorCount: number;
  };
  host?: HostMetricsBlock;
  vps?: HostMetricsBlock | null;
  vpsMetricsError?: string | null;
  app?: {
    version: string;
    environment: string;
    port: number;
    sentry: boolean;
    maintenance: boolean;
    maintenanceSince: string | null;
    redis: boolean;
  };
  checks?: InfraCheck[];
  reboot?: InfraRebootState | null;
  history?: InfraHistoryPayload;
  analytics?: AppAnalyticsSnapshot;
  fans?: FanStatusSnapshot;
  error?: string;
}

interface AppAnalyticsSnapshot {
  date: string;
  totalUsers: number;
  activeToday: number;
  newToday: number;
  onlineNow: number;
}

interface FanStatusSnapshot {
  available: boolean;
  mode: 'auto' | 'manual';
  percent: number;
  fan2Rpm: number | null;
  fan3Rpm: number | null;
  cpuTempC: number | null;
  appliedPwm: number | null;
  error?: string;
}

interface InfraHistoryPoint {
  t: number;
  v: number;
}

interface InfraHostHistory {
  cpu: InfraHistoryPoint[];
  ram: InfraHistoryPoint[];
  temp: InfraHistoryPoint[];
}

interface InfraHistoryPayload {
  home: InfraHostHistory;
  vps: InfraHostHistory;
  ping: Record<string, InfraHistoryPoint[]>;
}

type InfraRebootTarget = 'home' | 'vps' | 'all';

interface InfraRebootState {
  target: InfraRebootTarget;
  phase: 'idle' | 'rebooting' | 'waiting' | 'online' | 'failed';
  message: string;
  startedAt: string;
  updatedAt: string;
  completedAt?: string;
  homeOnline?: boolean;
  vpsOnline?: boolean;
}

let refreshTimer: ReturnType<typeof setInterval> | null = null;
let loading = false;
let pendingRebootTarget: InfraRebootTarget | null = null;
let lastHistory: InfraHistoryPayload | null = null;
let lastChecks: InfraCheck[] = [];
const clientHistory = new Map<string, ChartPoint[]>();
const CLIENT_HISTORY_MAX = 90;
const REFRESH_MS = 2000;
const ONLINE_REFRESH_MS = 1000;
const metricNumericState = new Map<string, number>();
let fanUiBusy = false;
let fanSaveTimer: ReturnType<typeof setTimeout> | null = null;
let lastFanMode: 'auto' | 'manual' = 'auto';
let infraContentRevealed = false;
let onlineTimer: ReturnType<typeof setInterval> | null = null;
let onlineRollCounter: ReturnType<typeof bindRollCounter> | null = null;
let lastOnlineCount = 0;
let lastAnalyticsSnapshot: AppAnalyticsSnapshot | null = null;
let maintenanceSinceMs: number | null = null;
let maintenanceTimer: ReturnType<typeof setInterval> | null = null;
let rebootStatusVisible = false;

function formatDuration(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (days > 0) return `${days}д ${hours}ч`;
  if (hours > 0) return `${hours}ч ${mins}м`;
  return `${mins}м`;
}

function formatUpdatedAt(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return '—';
  }
}

function statusClass(status: InfraCheckStatus): string {
  if (status === 'ok') return 'is-ok';
  if (status === 'warn') return 'is-warn';
  if (status === 'error') return 'is-error';
  return '';
}

function metricLevel(percent: number): string {
  if (percent >= 92) return 'is-bad';
  if (percent >= 80) return 'is-warn';
  return '';
}

function formatTemperature(host: HostMetricsBlock): { text: string; warn: boolean } {
  if (host.temperatureC != null) {
    return { text: `${host.temperatureC}°C`, warn: host.temperatureC >= 85 };
  }
  return { text: '—', warn: false };
}

function rebootTargetLabel(target: InfraRebootTarget): string {
  if (target === 'home') return 'основной сервер';
  if (target === 'vps') return 'VPS';
  return 'все серверы';
}

function applyMetricLevel(article: HTMLElement, percent: number): void {
  article.classList.remove('is-warn', 'is-bad');
  const lvl = metricLevel(percent);
  if (lvl) article.classList.add(lvl);
}

function animateMetricValue(
  scope: string,
  metricId: string,
  key: string,
  el: HTMLElement,
  value: number,
  format: (n: number) => string,
  durationMs = 700
): void {
  const stateKey = `${scope}:${metricId}:${key}`;
  const prev = metricNumericState.get(stateKey);
  const from = prev != null && Number.isFinite(prev) ? prev : value;
  metricNumericState.set(stateKey, value);
  tweenNumber(stateKey, from, value, durationMs, (v) => {
    el.textContent = format(v);
  });
}

function ensureCpuMetric(container: HTMLElement, scope: string): HTMLElement {
  const existing = container.querySelector('[data-metric-id="cpu"]');
  if (existing instanceof HTMLElement) return existing;

  const article = document.createElement('article');
  article.className = 'infra-metric infra-metric-chartable';
  article.dataset.metricId = 'cpu';
  article.dataset.infraChart = `${scope}:cpu`;
  article.setAttribute('role', 'button');
  article.tabIndex = 0;
  article.innerHTML =
    '<div class="infra-metric-head">' +
    '<span class="infra-metric-label">CPU (load)</span>' +
    '<span class="infra-metric-value">' +
    '<span data-val="pct">0</span>% · <span data-val="load">0</span> / <span data-val="cores">0</span> яд.' +
    '</span></div>' +
    '<div class="infra-metric-bar"><span data-metric-bar></span></div>' +
    '<span class="infra-metric-chart-hint">График ›</span>';
  container.appendChild(article);
  return article;
}

function ensureRamMetric(container: HTMLElement, scope: string): HTMLElement {
  const existing = container.querySelector('[data-metric-id="ram"]');
  if (existing instanceof HTMLElement) return existing;

  const article = document.createElement('article');
  article.className = 'infra-metric infra-metric-chartable';
  article.dataset.metricId = 'ram';
  article.dataset.infraChart = `${scope}:ram`;
  article.setAttribute('role', 'button');
  article.tabIndex = 0;
  article.innerHTML =
    '<div class="infra-metric-head">' +
    '<span class="infra-metric-label">RAM</span>' +
    '<span class="infra-metric-value">' +
    '<span data-val="used">0</span> / <span data-val="total">0</span> МБ' +
    '</span></div>' +
    '<div class="infra-metric-bar"><span data-metric-bar></span></div>' +
    '<span class="infra-metric-chart-hint">График ›</span>';
  container.appendChild(article);
  return article;
}

function ensureStaticMetric(container: HTMLElement, id: string, label: string): HTMLElement {
  const existing = container.querySelector(`[data-metric-id="${id}"]`);
  if (existing instanceof HTMLElement) return existing;

  const article = document.createElement('article');
  article.className = 'infra-metric';
  article.dataset.metricId = id;
  article.innerHTML =
    '<div class="infra-metric-head">' +
    '<span class="infra-metric-label">' +
    escapeHtmlText(label) +
    '</span>' +
    '<span class="infra-metric-value" data-metric-value></span>' +
    '</div>';
  container.appendChild(article);
  return article;
}

function ensureTempMetric(container: HTMLElement): HTMLElement {
  const existing = container.querySelector('[data-metric-id="temp"]');
  if (existing instanceof HTMLElement) return existing;

  const article = document.createElement('article');
  article.className = 'infra-metric infra-metric-chartable';
  article.dataset.metricId = 'temp';
  article.dataset.infraChart = 'home:temp';
  article.setAttribute('role', 'button');
  article.tabIndex = 0;
  article.innerHTML =
    '<div class="infra-metric-head">' +
    '<span class="infra-metric-label">Температура</span>' +
    '<span class="infra-metric-value" data-metric-value><span data-val="temp">—</span></span>' +
    '</div>' +
    '<span class="infra-metric-chart-hint">График ›</span>';
  container.appendChild(article);
  return article;
}

function ensureStaleNote(container: HTMLElement): HTMLElement {
  const existing = container.querySelector('[data-metric-stale]');
  if (existing instanceof HTMLElement) return existing;

  const note = document.createElement('p');
  note.className = 'infra-stale-note';
  note.dataset.metricStale = '1';
  note.hidden = true;
  container.appendChild(note);
  return note;
}

function renderHostMetrics(
  host: HostMetricsBlock,
  elementId: string,
  options: { processLabel: string; emptyMessage?: string; showTemperature?: boolean }
): void {
  const el = document.getElementById(elementId);
  if (!el) return;

  if (el.dataset.metricsShell !== '1') {
    el.innerHTML = '';
    el.dataset.metricsShell = '1';
  }

  const scope = elementId.includes('vps') ? 'vps' : 'home';
  const cpu = host.loadPercent ?? 0;

  const cpuArt = ensureCpuMetric(el, scope);
  const ramArt = ensureRamMetric(el, scope);
  ensureStaticMetric(el, 'uptime-os', 'Uptime ОС');
  ensureStaticMetric(el, 'uptime-proc', options.processLabel);
  if (options.showTemperature !== false) ensureTempMetric(el);
  ensureStaticMetric(el, 'hostname', 'Хост');
  const staleNote = ensureStaleNote(el);

  const cpuBar = cpuArt.querySelector('[data-metric-bar]');
  if (cpuBar instanceof HTMLElement) setBarWidthSmooth(cpuBar, cpu);
  applyMetricLevel(cpuArt, cpu);

  const pctEl = cpuArt.querySelector('[data-val="pct"]');
  const loadEl = cpuArt.querySelector('[data-val="load"]');
  const coresEl = cpuArt.querySelector('[data-val="cores"]');
  if (pctEl instanceof HTMLElement) {
    animateMetricValue(scope, 'cpu', 'pct', pctEl, cpu, (n) => String(Math.round(n)));
  }
  if (loadEl instanceof HTMLElement) {
    animateMetricValue(scope, 'cpu', 'load', loadEl, host.loadAvg1, (n) => n.toFixed(2));
  }
  if (coresEl) coresEl.textContent = String(host.cpuCount);

  const ramBar = ramArt.querySelector('[data-metric-bar]');
  if (ramBar instanceof HTMLElement) setBarWidthSmooth(ramBar, host.memoryUsedPercent);
  applyMetricLevel(ramArt, host.memoryUsedPercent);

  const usedEl = ramArt.querySelector('[data-val="used"]');
  const totalEl = ramArt.querySelector('[data-val="total"]');
  if (usedEl instanceof HTMLElement) {
    animateMetricValue(scope, 'ram', 'used', usedEl, host.memoryUsedMb, (n) => String(Math.round(n)));
  }
  if (totalEl) totalEl.textContent = String(host.memoryTotalMb);

  const uptimeOsEl = el.querySelector('[data-metric-id="uptime-os"] [data-metric-value]');
  if (uptimeOsEl) uptimeOsEl.textContent = formatDuration(host.uptimeSec);

  const uptimeProcEl = el.querySelector('[data-metric-id="uptime-proc"] [data-metric-value]');
  if (uptimeProcEl) uptimeProcEl.textContent = formatDuration(host.processUptimeSec);

  if (options.showTemperature !== false) {
    const tempArt = ensureTempMetric(el);
    const temp = formatTemperature(host);
    tempArt.classList.toggle('is-warn', temp.warn);
    const tempValEl = tempArt.querySelector('[data-val="temp"]');
    if (host.temperatureC != null && tempValEl instanceof HTMLElement) {
      animateMetricValue(scope, 'temp', 'c', tempValEl, host.temperatureC, (n) => `${Math.round(n)}°C`);
    } else if (tempValEl) {
      tempValEl.textContent = temp.text;
    }
  }

  const hostEl = el.querySelector('[data-metric-id="hostname"] [data-metric-value]');
  if (hostEl) hostEl.textContent = host.hostname;

  if (host.stale) {
    staleNote.hidden = false;
    staleNote.textContent = `Данные устарели (${host.ageSec ?? '?'} с)`;
  } else {
    staleNote.hidden = true;
  }
}

function markInfraContentLive(): void {
  const content = document.querySelector('#view-owner-infra-status .owner-infra-content');
  if (content) content.classList.add('is-live');
}

function revealInfraContent(): void {
  if (infraContentRevealed) return;
  const content = document.querySelector('#view-owner-infra-status .owner-infra-content');
  if (!content) return;
  infraContentRevealed = true;
  content.classList.remove('is-loading');
  requestAnimationFrame(() => {
    content.classList.add('is-ready');
    markInfraContentLive();
  });
}

function resetInfraContentReveal(): void {
  infraContentRevealed = false;
  const content = document.querySelector('#view-owner-infra-status .owner-infra-content');
  if (!content) return;
  content.classList.add('is-loading');
  content.classList.remove('is-ready', 'is-live');
}

function renderSummary(data: InfraStatusResponse): void {
  const summaryEl = document.getElementById('owner-infra-summary');
  const badgeEl = document.getElementById('owner-infra-summary-badge');
  const textEl = document.getElementById('owner-infra-summary-text');
  const updatedEl = document.getElementById('owner-infra-updated');
  if (!summaryEl || !badgeEl || !textEl) return;

  const overall = data.summary?.overall || 'down';
  summaryEl.classList.remove('is-ok', 'is-degraded', 'is-down');
  summaryEl.classList.add(
    overall === 'ok' ? 'is-ok' : overall === 'degraded' ? 'is-degraded' : 'is-down'
  );

  const labels = { ok: 'Всё в порядке', degraded: 'Есть предупреждения', down: 'Есть проблемы' };
  badgeEl.textContent = labels[overall];
  const ok = data.summary?.okCount ?? 0;
  const warn = data.summary?.warnCount ?? 0;
  const err = data.summary?.errorCount ?? 0;
  textEl.textContent = `OK: ${ok} · предупреждения: ${warn} · ошибки: ${err}`;
  if (updatedEl && data.collectedAt) {
    updatedEl.textContent = `Обновлено ${formatUpdatedAt(data.collectedAt)}`;
  }
}

function renderVpsMetrics(vps: HostMetricsBlock | null | undefined, error: string | null | undefined): void {
  const el = document.getElementById('owner-infra-vps-metrics');
  if (!el) return;
  if (!vps) {
    delete el.dataset.metricsShell;
    el.innerHTML =
      '<p class="infra-stale-note">' +
      escapeHtmlText(error || 'Метрики VPS недоступны. Задайте INFRA_VPS_METRICS_URL в app/.env') +
      '</p>';
    return;
  }
  renderHostMetrics(vps, 'owner-infra-vps-metrics', { processLabel: 'Uptime nginx', showTemperature: false });
}

function renderApp(app: NonNullable<InfraStatusResponse['app']>): void {
  const el = document.getElementById('owner-infra-app-info');
  if (!el) return;
  const rows = [
    ['Версия', app.version],
    ['Окружение', app.environment],
    ['Порт', String(app.port)],
    ['Redis', app.redis ? 'Подключён' : 'Нет'],
    ['Sentry', app.sentry ? 'Вкл' : 'Выкл'],
    ['Техработы', app.maintenance ? 'Вкл' : 'Выкл']
  ];
  el.innerHTML = rows
    .map(function (row) {
      return (
        '<dl class="infra-kv-row"><dt>' +
        escapeHtmlText(row[0]) +
        '</dt><dd>' +
        escapeHtmlText(row[1]) +
        '</dd></dl>'
      );
    })
    .join('');
}

function ensureCheckArticle(el: HTMLElement, check: InfraCheck): HTMLElement {
  const existing = el.querySelector(`[data-check-id="${check.id}"]`);
  if (existing instanceof HTMLElement) return existing;

  const pingChart =
    check.group === 'endpoints' && check.latencyMs != null && check.latencyMs >= 0
      ? ' infra-check-chartable'
      : '';
  const chartAttr =
    check.group === 'endpoints' && check.latencyMs != null && check.latencyMs >= 0
      ? ` data-infra-chart="ping:${escapeHtmlText(check.id)}" role="button" tabindex="0"`
      : '';

  const article = document.createElement('article');
  article.className = 'infra-check' + pingChart;
  article.dataset.checkId = check.id;
  if (chartAttr) {
    article.dataset.infraChart = `ping:${check.id}`;
    article.setAttribute('role', 'button');
    article.tabIndex = 0;
  }
  article.innerHTML =
    '<span class="infra-check-dot" aria-hidden="true"></span>' +
    '<div class="infra-check-body">' +
    '<div class="infra-check-name"></div>' +
    '<div class="infra-check-detail"></div>' +
    '</div>' +
    '<div class="infra-check-latency" data-check-latency></div>';
  el.appendChild(article);
  return article;
}

function renderChecks(checks: InfraCheck[]): void {
  const el = document.getElementById('owner-infra-checks');
  if (!el) return;
  const order = ['services', 'host', 'endpoints'];
  const sorted = checks.slice().sort(function (a, b) {
    const ga = order.indexOf(a.group);
    const gb = order.indexOf(b.group);
    return (ga === -1 ? 99 : ga) - (gb === -1 ? 99 : gb);
  });

  const ids = new Set(sorted.map((c) => c.id));
  el.querySelectorAll('[data-check-id]').forEach((node) => {
    const id = (node as HTMLElement).dataset.checkId;
    if (id && !ids.has(id)) node.remove();
  });

  sorted.forEach(function (check) {
    const article = ensureCheckArticle(el, check);
    article.className =
      'infra-check ' +
      statusClass(check.status) +
      (check.group === 'endpoints' && check.latencyMs != null && check.latencyMs >= 0
        ? ' infra-check-chartable'
        : '');

    const nameEl = article.querySelector('.infra-check-name');
    const detailEl = article.querySelector('.infra-check-detail');
    const latencyEl = article.querySelector('[data-check-latency]');
    if (nameEl) nameEl.textContent = check.name;
    if (detailEl) detailEl.textContent = check.detail || '';

    if (latencyEl instanceof HTMLElement) {
      if (check.id === 'maintenance' && check.status === 'warn' && maintenanceSinceMs != null) {
        latencyEl.textContent = formatMaintenanceElapsed((Date.now() - maintenanceSinceMs) / 1000);
        latencyEl.classList.add('infra-check-maintenance-timer');
      } else if (check.latencyMs != null && check.latencyMs >= 0) {
        latencyEl.classList.remove('infra-check-maintenance-timer');
        animateMetricValue('checks', check.id, 'ms', latencyEl, check.latencyMs, (n) =>
          `${Math.round(n)} мс`
        );
      } else {
        latencyEl.classList.remove('infra-check-maintenance-timer');
        latencyEl.textContent = '—';
      }
    }
  });
}

function ensureOnlineRollCounter(): ReturnType<typeof bindRollCounter> | null {
  const el = document.getElementById('owner-infra-users-online');
  if (!(el instanceof HTMLElement)) return null;
  if (!onlineRollCounter) onlineRollCounter = bindRollCounter(el);
  return onlineRollCounter;
}

function setOnlineCount(count: number, animate = true): void {
  const counter = ensureOnlineRollCounter();
  if (!counter) return;
  const prev = counter.get();
  counter.set(count, animate && prev >= 0);
  lastOnlineCount = count;
  syncAnalyticsHint();
}

function syncAnalyticsHint(): void {
  const hintEl = document.getElementById('owner-infra-users-hint');
  if (!hintEl || !lastAnalyticsSnapshot) return;
  const analytics = { ...lastAnalyticsSnapshot, onlineNow: lastOnlineCount };
  const day = analytics.date || 'сегодня';
  const onlinePart =
    analytics.onlineNow > 0
      ? `Сейчас онлайн: ${analytics.onlineNow}. `
      : 'Сейчас онлайн: 0. ';
  if (analytics.newToday > 0) {
    hintEl.textContent = `${onlinePart}${day}: открыли ${analytics.activeToday} чел., из них ${analytics.newToday} впервые.`;
  } else {
    hintEl.textContent = `${onlinePart}${day}: приложение открыли ${analytics.activeToday} чел., новых пользователей нет.`;
  }
}

async function pollOnlineCount(): Promise<void> {
  try {
    const data = await apiFetchJSON<{ ok: boolean; onlineNow: number }>('/api/owner/infra/online', {
      method: 'GET',
      cache: 'no-store'
    });
    if (data?.ok && Number.isFinite(data.onlineNow)) {
      setOnlineCount(data.onlineNow);
    }
  } catch {
    /* polling retries */
  }
}

function startOnlinePolling(): void {
  if (onlineTimer) return;
  void pollOnlineCount();
  onlineTimer = setInterval(function () {
    const view = document.getElementById('view-owner-infra-status');
    if (view && view.classList.contains('view-active')) {
      void pollOnlineCount();
    }
  }, ONLINE_REFRESH_MS);
}

function stopOnlinePolling(): void {
  if (onlineTimer) {
    clearInterval(onlineTimer);
    onlineTimer = null;
  }
}

function syncMaintenanceSince(app: InfraStatusResponse['app']): void {
  if (app?.maintenance && app.maintenanceSince) {
    const ms = Date.parse(app.maintenanceSince);
    maintenanceSinceMs = Number.isFinite(ms) ? ms : null;
  } else {
    maintenanceSinceMs = null;
  }
  if (maintenanceSinceMs != null) startMaintenanceTimer();
  else stopMaintenanceTimer();
}

function updateMaintenanceTimerEl(): void {
  if (maintenanceSinceMs == null) return;
  const el = document.querySelector('[data-check-id="maintenance"] [data-check-latency]');
  if (!(el instanceof HTMLElement)) return;
  el.textContent = formatMaintenanceElapsed((Date.now() - maintenanceSinceMs) / 1000);
}

function startMaintenanceTimer(): void {
  if (maintenanceTimer) return;
  updateMaintenanceTimerEl();
  maintenanceTimer = setInterval(function () {
    const view = document.getElementById('view-owner-infra-status');
    if (view && view.classList.contains('view-active')) {
      updateMaintenanceTimerEl();
    }
  }, 1000);
}

function stopMaintenanceTimer(): void {
  if (maintenanceTimer) {
    clearInterval(maintenanceTimer);
    maintenanceTimer = null;
  }
}

function renderAnalytics(analytics: AppAnalyticsSnapshot | undefined): void {
  if (!analytics) return;
  lastAnalyticsSnapshot = analytics;
  const totalEl = document.getElementById('owner-infra-users-total');
  const todayEl = document.getElementById('owner-infra-users-today');
  const newEl = document.getElementById('owner-infra-users-new');
  setOnlineCount(analytics.onlineNow, false);
  if (totalEl instanceof HTMLElement) {
    animateMetricValue('users', 'all', 'total', totalEl, analytics.totalUsers, (n) =>
      String(Math.round(n))
    );
  }
  if (todayEl instanceof HTMLElement) {
    animateMetricValue('users', 'day', 'active', todayEl, analytics.activeToday, (n) =>
      String(Math.round(n))
    );
  }
  if (newEl instanceof HTMLElement) {
    animateMetricValue('users', 'day', 'new', newEl, analytics.newToday, (n) => String(Math.round(n)));
  }
  syncAnalyticsHint();
}

function renderFans(fans: FanStatusSnapshot | undefined): void {
  const rpmEl = document.getElementById('owner-infra-fans-rpm');
  const autoBtn = document.getElementById('owner-infra-fans-auto');
  const manualBtn = document.getElementById('owner-infra-fans-manual');
  const sliderWrap = document.getElementById('owner-infra-fans-slider-wrap');
  const slider = document.getElementById('owner-infra-fans-slider');
  const percentEl = document.getElementById('owner-infra-fans-percent');
  const hintEl = document.getElementById('owner-infra-fans-hint');
  if (!fans) return;

  if (rpmEl) {
    if (!fans.available) {
      rpmEl.textContent = fans.error || 'Управление недоступно на этом хосте';
    } else {
      const cpu = fans.fan2Rpm != null ? `${fans.fan2Rpm} RPM` : '—';
      const caseFan = fans.fan3Rpm != null ? `${fans.fan3Rpm} RPM` : '—';
      const temp = fans.cpuTempC != null ? ` · CPU ${Math.round(fans.cpuTempC)}°C` : '';
      rpmEl.textContent = `CPU ${cpu} · корпус ${caseFan}${temp}`;
    }
  }

  if (autoBtn) autoBtn.classList.toggle('is-active', fans.mode === 'auto');
  if (manualBtn) manualBtn.classList.toggle('is-active', fans.mode === 'manual');
  if (sliderWrap) sliderWrap.classList.toggle('is-disabled', fans.mode === 'auto');
  lastFanMode = fans.mode;

  if (!fanUiBusy && slider instanceof HTMLInputElement) {
    slider.value = String(fans.percent);
    if (percentEl) percentEl.textContent = `${fans.percent}%`;
  }

  if (hintEl && fans.available) {
    hintEl.textContent =
      fans.mode === 'auto'
        ? 'Авто: скорость по температуре CPU (тише при простое).'
        : `Ручной режим: ${fans.percent}% от диапазона вентиляторов.`;
  }
}

async function saveFanControl(mode: 'auto' | 'manual', percent?: number): Promise<void> {
  fanUiBusy = true;
  try {
    const body: { mode: string; percent?: number } = { mode };
    if (mode === 'manual' && percent != null) body.percent = percent;
    const data = await apiFetchJSON<{ ok: boolean; fans?: FanStatusSnapshot }>(
      '/api/owner/infra/fans',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }
    );
    if (data?.fans) renderFans(data.fans);
  } catch {
    /* polling restores state */
  } finally {
    window.setTimeout(() => {
      fanUiBusy = false;
    }, 500);
  }
}

function scheduleFanPercentSave(percent: number): void {
  if (fanSaveTimer) clearTimeout(fanSaveTimer);
  fanSaveTimer = setTimeout(() => {
    fanSaveTimer = null;
    void saveFanControl('manual', percent);
  }, 450);
}

function bindFanControls(): void {
  const view = document.getElementById('view-owner-infra-status');
  if (!view || view.dataset.infraFansBound === '1') return;
  view.dataset.infraFansBound = '1';

  const autoBtn = document.getElementById('owner-infra-fans-auto');
  const manualBtn = document.getElementById('owner-infra-fans-manual');
  const slider = document.getElementById('owner-infra-fans-slider');
  const percentEl = document.getElementById('owner-infra-fans-percent');

  autoBtn?.addEventListener('click', () => {
    void saveFanControl('auto');
  });
  manualBtn?.addEventListener('click', () => {
    const pct =
      slider instanceof HTMLInputElement ? Number(slider.value) || 30 : 30;
    void saveFanControl('manual', pct);
  });
  if (slider instanceof HTMLInputElement) {
    slider.addEventListener('input', () => {
      const pct = Number(slider.value) || 30;
      if (percentEl) percentEl.textContent = `${pct}%`;
      if (lastFanMode === 'manual') scheduleFanPercentSave(pct);
    });
    slider.addEventListener('change', () => {
      const pct = Number(slider.value) || 30;
      if (lastFanMode !== 'manual') {
        void saveFanControl('manual', pct);
      } else {
        scheduleFanPercentSave(pct);
      }
    });
  }
}

bindFanControls();

function getInfraBottomScrollInset(): number {
  const tabbar = document.querySelector('.app-tabbar:not(.app-tabbar-hidden)');
  let inset = 16;
  if (tabbar) inset = 72;
  const ping = document.getElementById('owner-chat-ping-stat');
  if (ping && !ping.hidden) inset = 92;
  return inset + 20;
}

function scrollInfraRebootIntoView(focus?: HTMLElement | null): void {
  const confirmEl = document.getElementById('owner-infra-reboot-confirm');
  const menuEl = document.getElementById('owner-infra-reboot-menu');
  const statusEl = document.getElementById('owner-infra-reboot-status');
  const panel = document.getElementById('owner-infra-reboot-panel');
  const target =
    focus ??
    (confirmEl && !confirmEl.hidden ? confirmEl : null) ??
    (menuEl && !menuEl.hidden ? menuEl : null) ??
    (statusEl && !statusEl.hidden ? statusEl : null) ??
    panel;
  if (!(target instanceof HTMLElement)) return;

  window.requestAnimationFrame(function () {
    window.requestAnimationFrame(function () {
      const bottomLimit = window.innerHeight - getInfraBottomScrollInset();
      const rect = target.getBoundingClientRect();
      if (rect.bottom > bottomLimit) {
        window.scrollBy({ top: rect.bottom - bottomLimit, behavior: 'smooth' });
      }
    });
  });
}

function renderRebootState(reboot: InfraRebootState | null | undefined): void {
  const statusEl = document.getElementById('owner-infra-reboot-status');
  const toggleEl = document.getElementById('owner-infra-reboot-toggle');
  const menuEl = document.getElementById('owner-infra-reboot-menu');
  const confirmEl = document.getElementById('owner-infra-reboot-confirm');
  if (!statusEl || !toggleEl || !menuEl) return;

  const busy =
    reboot && (reboot.phase === 'rebooting' || reboot.phase === 'waiting' || reboot.phase === 'online');
  if (toggleEl instanceof HTMLButtonElement) toggleEl.disabled = !!busy;
  if (busy) {
    menuEl.hidden = true;
    toggleEl.setAttribute('aria-expanded', 'false');
    if (confirmEl) confirmEl.hidden = true;
  }

  if (!reboot || reboot.phase === 'idle') {
    statusEl.hidden = true;
    rebootStatusVisible = false;
    return;
  }

  const wasVisible = rebootStatusVisible;
  statusEl.hidden = false;
  rebootStatusVisible = true;
  statusEl.classList.remove('is-waiting', 'is-online', 'is-failed');
  let title = 'Перезагрузка';
  if (reboot.phase === 'waiting') {
    statusEl.classList.add('is-waiting');
    title = 'Ожидание';
  } else if (reboot.phase === 'online') {
    statusEl.classList.add('is-online');
    title = 'Готово';
  } else if (reboot.phase === 'failed') {
    statusEl.classList.add('is-failed');
    title = 'Ошибка';
  }

  const parts = [reboot.message];
  if (reboot.homeOnline != null || reboot.vpsOnline != null) {
    const home = reboot.homeOnline ? 'основной ✓' : 'основной …';
    const vps = reboot.vpsOnline ? 'VPS ✓' : 'VPS …';
    if (reboot.target === 'all') parts.push(`${home} · ${vps}`);
    else if (reboot.target === 'home') parts.push(home);
    else parts.push(vps);
  }

  statusEl.innerHTML =
    '<div class="infra-reboot-status-title">' +
    escapeHtmlText(title) +
    '</div>' +
    escapeHtmlText(parts.join(' · '));

  if (!wasVisible) scrollInfraRebootIntoView(statusEl);
}

function setRebootMenuOpen(open: boolean): void {
  const toggleEl = document.getElementById('owner-infra-reboot-toggle');
  const menuEl = document.getElementById('owner-infra-reboot-menu');
  const confirmEl = document.getElementById('owner-infra-reboot-confirm');
  if (!toggleEl || !menuEl) return;
  menuEl.hidden = !open;
  toggleEl.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (!open && confirmEl) confirmEl.hidden = true;
  if (open) scrollInfraRebootIntoView(menuEl);
}

function showRebootConfirm(target: InfraRebootTarget): void {
  pendingRebootTarget = target;
  const menuEl = document.getElementById('owner-infra-reboot-menu');
  const confirmEl = document.getElementById('owner-infra-reboot-confirm');
  const textEl = document.getElementById('owner-infra-reboot-confirm-text');
  if (menuEl) menuEl.hidden = true;
  if (confirmEl) confirmEl.hidden = false;
  if (textEl) {
    textEl.textContent = `Перезагрузить ${rebootTargetLabel(target)}? Мини-апп может отключиться на 1–3 минуты.`;
  }
  scrollInfraRebootIntoView(confirmEl ?? undefined);
}

async function submitReboot(): Promise<void> {
  if (!pendingRebootTarget) return;
  const target = pendingRebootTarget;
  pendingRebootTarget = null;
  const confirmEl = document.getElementById('owner-infra-reboot-confirm');
  const goBtn = document.getElementById('owner-infra-reboot-go');
  if (confirmEl) confirmEl.hidden = true;
  if (goBtn instanceof HTMLButtonElement) goBtn.disabled = true;
  try {
    await apiFetchJSON('/api/owner/infra/reboot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target, confirm: true })
    });
  } catch {
    /* polling picks up state */
  } finally {
    if (goBtn instanceof HTMLButtonElement) goBtn.disabled = false;
    loadOwnerInfraStatus();
  }
}

function bindRebootControls(): void {
  const toggleEl = document.getElementById('owner-infra-reboot-toggle');
  const menuEl = document.getElementById('owner-infra-reboot-menu');
  const cancelEl = document.getElementById('owner-infra-reboot-cancel');
  const goEl = document.getElementById('owner-infra-reboot-go');
  if (toggleEl && menuEl) {
    toggleEl.addEventListener('click', function () {
      if (toggleEl instanceof HTMLButtonElement && toggleEl.disabled) return;
      setRebootMenuOpen(menuEl.hidden);
    });
  }
  if (menuEl) {
    menuEl.querySelectorAll('[data-reboot-target]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const target = (btn as HTMLElement).dataset.rebootTarget as InfraRebootTarget | undefined;
        if (!target) return;
        showRebootConfirm(target);
      });
    });
  }
  if (cancelEl) {
    cancelEl.addEventListener('click', function () {
      pendingRebootTarget = null;
      const confirmEl = document.getElementById('owner-infra-reboot-confirm');
      if (confirmEl) confirmEl.hidden = true;
      setRebootMenuOpen(false);
    });
  }
  if (goEl) {
    goEl.addEventListener('click', function () {
      submitReboot();
    });
  }
}

bindRebootControls();

function pushClientSample(key: string, value: number | null | undefined): void {
  if (value == null || !Number.isFinite(value)) return;
  const now = Date.now();
  const list = clientHistory.get(key) ?? [];
  const last = list[list.length - 1];
  if (last && now - last.t < 10_000) {
    last.t = now;
    last.v = value;
  } else {
    list.push({ t: now, v: value });
  }
  while (list.length > CLIENT_HISTORY_MAX) list.shift();
  clientHistory.set(key, list);
}

function recordClientHistory(data: InfraStatusResponse): void {
  if (data.host) {
    pushClientSample('home:cpu', data.host.loadPercent);
    pushClientSample('home:ram', data.host.memoryUsedPercent);
    pushClientSample('home:temp', data.host.temperatureC);
  }
  if (data.vps) {
    pushClientSample('vps:cpu', data.vps.loadPercent);
    pushClientSample('vps:ram', data.vps.memoryUsedPercent);
  }
  for (const check of data.checks ?? []) {
    if (check.group === 'endpoints' && check.latencyMs != null && check.latencyMs >= 0) {
      pushClientSample(`ping:${check.id}`, check.latencyMs);
    }
  }
}

function serverPointsForKey(history: InfraHistoryPayload | null, key: string): ChartPoint[] {
  if (!history) return [];
  if (key === 'home:cpu') return history.home.cpu;
  if (key === 'home:ram') return history.home.ram;
  if (key === 'home:temp') return history.home.temp;
  if (key === 'vps:cpu') return history.vps.cpu;
  if (key === 'vps:ram') return history.vps.ram;
  if (key.startsWith('ping:')) {
    const id = key.slice(5);
    return history.ping[id] ?? [];
  }
  return [];
}

function mergedPointsForKey(key: string): ChartPoint[] {
  const server = serverPointsForKey(lastHistory, key);
  const client = clientHistory.get(key) ?? [];
  return mergeChartHistory(server, client);
}

function openChartForKey(key: string): void {
  const preset = INFRA_CHART_PRESETS[key];
  if (preset) {
    openInfraChart(key, mergedPointsForKey(key), {
      title: preset.title || key,
      unit: preset.unit,
      min: preset.min,
      max: preset.max,
      color: preset.color,
      fill: preset.fill
    });
    return;
  }
  if (key.startsWith('ping:')) {
    const id = key.slice(5);
    const check = lastChecks.find((c) => c.id === id);
    openInfraChart(
      key,
      mergedPointsForKey(key),
      chartOptionsForPing(id, check?.name || id)
    );
  }
}

function refreshOpenChartIfNeeded(): void {
  const key = getOpenInfraChartKey();
  if (!key) return;
  const preset = INFRA_CHART_PRESETS[key];
  if (preset) {
    refreshOpenInfraChart(key, mergedPointsForKey(key), {
      title: preset.title || key,
      unit: preset.unit,
      min: preset.min,
      max: preset.max,
      color: preset.color,
      fill: preset.fill
    });
    return;
  }
  if (key.startsWith('ping:')) {
    const id = key.slice(5);
    const check = lastChecks.find((c) => c.id === id);
    refreshOpenInfraChart(
      key,
      mergedPointsForKey(key),
      chartOptionsForPing(id, check?.name || id)
    );
  }
}

function handleInfraChartActivate(el: HTMLElement): void {
  const key = el.dataset.infraChart;
  if (!key) return;
  openChartForKey(key);
}

function bindInfraChartControls(): void {
  const view = document.getElementById('view-owner-infra-status');
  if (!view || view.dataset.infraChartsBound === '1') return;
  view.dataset.infraChartsBound = '1';
  view.addEventListener('click', function (ev) {
    const target = (ev.target as HTMLElement | null)?.closest('[data-infra-chart]');
    if (!(target instanceof HTMLElement)) return;
    handleInfraChartActivate(target);
  });
  view.addEventListener('keydown', function (ev) {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const target = (ev.target as HTMLElement | null)?.closest('[data-infra-chart]');
    if (!(target instanceof HTMLElement)) return;
    ev.preventDefault();
    handleInfraChartActivate(target);
  });
}

bindInfraChartControls();

function renderError(message: string): void {
  const summaryEl = document.getElementById('owner-infra-summary');
  const badgeEl = document.getElementById('owner-infra-summary-badge');
  const textEl = document.getElementById('owner-infra-summary-text');
  if (summaryEl) {
    summaryEl.classList.remove('is-ok', 'is-degraded');
    summaryEl.classList.add('is-down');
  }
  if (badgeEl) badgeEl.textContent = 'Не удалось загрузить';
  if (textEl) textEl.textContent = message;
}

export async function loadOwnerInfraStatus(): Promise<void> {
  if (loading) return;
  loading = true;
  try {
    const data = await apiFetchJSON<InfraStatusResponse>('/api/owner/infra-status', {
      method: 'GET',
      cache: 'no-store'
    });
    if (!data?.ok) {
      renderError(data?.error === 'forbidden' ? 'Доступ только для владельца' : 'Ошибка API');
      return;
    }
    renderSummary(data);
    lastHistory = data.history ?? null;
    lastChecks = data.checks ?? [];
    recordClientHistory(data);
    if (data.host) renderHostMetrics(data.host, 'owner-infra-host-metrics', { processLabel: 'Uptime Node' });
    renderVpsMetrics(data.vps, data.vpsMetricsError);
    if (data.app) {
      syncMaintenanceSince(data.app);
      renderApp(data.app);
    }
    if (data.checks) renderChecks(data.checks);
    renderRebootState(data.reboot);
    renderAnalytics(data.analytics);
    renderFans(data.fans);
    refreshOpenChartIfNeeded();
    revealInfraContent();
  } catch {
    renderError('Сеть или сервер недоступны');
    revealInfraContent();
  } finally {
    loading = false;
  }
}

export function initOwnerInfraStatusView(): void {
  resetInfraContentReveal();
  loadOwnerInfraStatus();
  startOnlinePolling();
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
  refreshTimer = setInterval(function () {
    const view = document.getElementById('view-owner-infra-status');
    if (view && view.classList.contains('view-active')) {
      loadOwnerInfraStatus();
    }
  }, REFRESH_MS);
}

export function stopOwnerInfraStatusPolling(): void {
  stopOnlinePolling();
  stopMaintenanceTimer();
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
  closeInfraChart();
}

const refreshBtn = document.getElementById('owner-infra-refresh');
if (refreshBtn) {
  refreshBtn.addEventListener('click', function () {
    loadOwnerInfraStatus();
  });
}
